import { PenLine } from "lucide-react";

import { useCallback, useState } from "react";

import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { getSmileSimulationCopy } from "../model/i18n";
import type { SmileSimulationLanguage } from "../model/types";
import { CaptureActions } from "./CaptureActions";
import { ExperienceSimulationLayout } from "./ExperienceSimulationLayout";
import { SmileSimulationPatientSummary } from "./SmileSimulationPatientSummary";
import { SmileSimulationViewport } from "./SmileSimulationViewport";

type SmileSimulationResultPageProps = {
  camera: SmileSimulationController["camera"];
  capture: SmileSimulationController["capture"];
  comparison: SmileSimulationController["comparison"];
  download: SmileSimulationController["download"];
  isExperience?: boolean;
  language: SmileSimulationLanguage;
  media: SmileSimulationController["media"];
  onDownloadResultImage: SmileSimulationController["actions"]["downloadCurrentResultImage"];
  onOpenPatientDetail: SmileSimulationController["actions"]["openCurrentPatientDetail"];
  simulation: SmileSimulationController["simulation"];
  onRetake: SmileSimulationController["actions"]["retake"];
  onRetrySimulation: SmileSimulationController["actions"]["retry"];
  onStartSimulation: SmileSimulationController["actions"]["startSimulation"];
  patient: NonNullable<SmileSimulationController["patient"]["current"]>;
};

export function SmileSimulationResultPage({
  camera,
  capture,
  comparison,
  download,
  isExperience = false,
  language,
  media,
  onDownloadResultImage,
  onOpenPatientDetail,
  simulation,
  onRetake,
  onRetrySimulation,
  onStartSimulation,
  patient,
}: SmileSimulationResultPageProps) {
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const copy = getSmileSimulationCopy(language);
  const resultTitle = copy.result[simulation.status];
  const goExperienceHome = useCallback(() => {
    window.location.assign("/experience");
  }, []);

  if (isExperience) {
    const experienceTitle =
      simulation.status === "completed" ? (
        <>
          내일의 미소를 <span className="text-[#8F14E5]">드래그</span>하여
          확인해보세요.
        </>
      ) : simulation.status === "processing" ? (
        <>
          <span className="text-[#8F14E5]">시뮬레이션</span>을 만들고 있습니다.
        </>
      ) : (
        <>
          이 사진으로 <span className="text-[#8F14E5]">디지털 진료</span>를
          받으시겠습니까?
        </>
      );
    const experienceFooter = (() => {
      if (simulation.status === "idle") {
        return (
          <div className="grid w-full grid-cols-[0.9fr_1.35fr] gap-4 sm:gap-5">
            <button
              type="button"
              onClick={onRetake}
              className="h-14 rounded-[1.25rem] bg-[#eeeeee] px-5 text-lg font-bold text-[#8F14E5] transition hover:bg-[#e5e5e5] sm:h-16 sm:text-xl"
            >
              다시 찍기
            </button>
            <button
              type="button"
              onClick={onStartSimulation}
              className="h-14 rounded-[1.25rem] bg-[#8F14E5] px-5 text-lg font-bold text-white shadow-[0_16px_34px_rgba(143,20,229,0.24)] transition hover:bg-[#7d10ca] sm:h-16 sm:text-xl"
            >
              디지털 진료 시작
            </button>
          </div>
        );
      }

      if (simulation.status === "processing") {
        return (
          <div className="flex w-full justify-center">
            <button
              type="button"
              onClick={onRetake}
              className="h-14 rounded-[1.25rem] bg-[#eeeeee] px-14 text-lg font-bold text-[#8F14E5] transition hover:bg-[#e5e5e5] sm:h-16 sm:text-xl"
            >
              다시 찍기
            </button>
          </div>
        );
      }

      if (simulation.status === "completed") {
        return (
          <div className="grid w-full grid-cols-[0.9fr_1.35fr] gap-4 sm:gap-5">
            <button
              type="button"
              onClick={onRetake}
              className="h-14 rounded-[1.25rem] bg-[#eeeeee] px-5 text-lg font-bold text-[#8F14E5] transition hover:bg-[#e5e5e5] sm:h-16 sm:text-xl"
            >
              다시 찍기
            </button>
            <button
              type="button"
              onClick={onDownloadResultImage}
              disabled={download.isDownloadingImage}
              className="h-14 rounded-[1.25rem] bg-[#8F14E5] px-5 text-lg font-bold text-white shadow-[0_16px_34px_rgba(143,20,229,0.24)] transition hover:bg-[#7d10ca] sm:h-16 sm:text-xl"
            >
              {download.isDownloadingImage ? "저장 중..." : "이미지 저장"}
            </button>
          </div>
        );
      }

      return (
        <CaptureActions
          language={language}
          controls={{
            isCaptureDisabled: capture.isDisabled,
            onCapture: () => undefined,
            onRetake,
            onRetrySimulation,
            onStartSimulation,
            onUploadImage: async () => undefined,
            step: "result",
            simulationStatus: simulation.status,
          }}
        />
      );
    })();

    return (
      <>
        <ExperienceSimulationLayout
          title={experienceTitle}
          footer={experienceFooter}
          onHomeClick={goExperienceHome}
        >
          <SmileSimulationViewport
            camera={camera}
            capture={capture}
            comparison={comparison}
            isFrameFixed
            language={language}
            media={media}
            progressVariant="experience"
            step="result"
            simulation={simulation}
          />
        </ExperienceSimulationLayout>
      </>
    );
  }

  return (
    <section className="flex h-full min-h-0 w-full flex-1 flex-col items-center justify-center gap-4 sm:gap-6">
      <div className="flex w-full items-center justify-end gap-3">
        <div className="flex flex-wrap justify-end gap-2">
          {simulation.status === "completed" && !isExperience ? (
            <>
              <button
                type="button"
                onClick={onDownloadResultImage}
                disabled={download.isDownloadingImage}
                className="rounded-full border border-[#8F14E5]/20 bg-white/90 px-4 py-2 text-sm font-semibold text-[#8F14E5] shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {download.isDownloadingImage
                  ? copy.result.downloading
                  : copy.result.download}
              </button>
              <button
                type="button"
                aria-label="Toggle drawing tools"
                aria-pressed={isDrawingEnabled}
                onClick={() =>
                  setIsDrawingEnabled(
                    (currentIsDrawingEnabled) => !currentIsDrawingEnabled,
                  )
                }
                className={`flex h-10 w-10 items-center justify-center rounded-full border shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition ${
                  isDrawingEnabled
                    ? "border-[#8F14E5] bg-[#8F14E5] text-white"
                    : "border-[#8F14E5]/20 bg-white/90 text-[#8F14E5] hover:bg-white"
                }`}
              >
                <PenLine className="h-4 w-4" />
              </button>
            </>
          ) : null}
          {simulation.status === "completed" && !isExperience ? (
            <button
              type="button"
              onClick={onOpenPatientDetail}
              className="rounded-full border border-[#8F14E5]/20 bg-white/90 px-4 py-2 text-sm font-semibold text-[#8F14E5] shadow-[0_12px_30px_rgba(143,20,229,0.12)] transition hover:bg-white"
            >
              {copy.result.openPatientHistory}
            </button>
          ) : null}
        </div>
      </div>

      {!isExperience ? (
        <SmileSimulationPatientSummary
          caption={copy.capture.currentPatient}
          language={language}
          patient={patient}
        />
      ) : null}

      {resultTitle ? (
        <div className="text-center">
          <h2 className="text-sm font-medium text-[#5f3a78]">{resultTitle}</h2>
        </div>
      ) : null}

      <SmileSimulationViewport
        camera={camera}
        capture={capture}
        comparison={comparison}
        isDrawingEnabled={simulation.status === "completed" && isDrawingEnabled}
        language={language}
        media={media}
        step="result"
        simulation={simulation}
      />

      <CaptureActions
        language={language}
        controls={{
          isCaptureDisabled: capture.isDisabled,
          onCapture: () => undefined,
          onRetake,
          onRetrySimulation,
          onStartSimulation,
          onUploadImage: async () => undefined,
          step: "result",
          simulationStatus: simulation.status,
        }}
      />
    </section>
  );
}
